/**
 * Vocabulary drill strategy (Phase 3, design §1) — pure ports of today's
 * Vocabulary branches. BUG-35 fixed: every place resolves the direction via
 * `isTargetToSource` (a missing `direction` means 'Source to Target').
 */
import type { DrillVocabulary, DrillQaSettings } from 'src/types/components';
import type { DrillQaReplyMethod } from 'src/types/course/drill';
import type { Try } from 'src/types/course/step';
import { orderArray } from 'src/shared/utilities/array';
import { isTypedAnswerCorrect } from 'src/modules/Step/views/StepView/utilities/typedAnswer';
import { escapeText } from './escapeText';
import { isTargetToSource } from './direction';
import type {
  DrillContext,
  DrillItemMap,
  DrillResultRowMap,
  DrillStrategy,
} from './types';

type VocabularyItem = DrillItemMap['Vocabulary'];

/** useStep `currentCorrectAnswer` (Vocabulary branch). */
function correctAnswer(item: VocabularyItem, ctx: DrillContext): string | null {
  const direction = isTargetToSource(ctx.drillType?.qa_settings)
    ? 'source'
    : 'target';
  return item && direction in item
    ? (item[direction as keyof typeof item] as string)
    : null;
}

const errorRow: DrillResultRowMap['Vocabulary'] = {
  id: -1,
  source: undefined,
  correct: undefined,
  tries: [],
};

export const vocabularyStrategy: DrillStrategy<'Vocabulary'> = {
  kind: 'Vocabulary',
  speaksQuestion: true,
  speaksAnswer: true,
  errorRow,

  correctAnswer,

  isCorrect(answer, item, ctx) {
    if (answer === undefined || answer === null) return false;
    const correct = correctAnswer(item, ctx);
    return (
      typeof correct === 'string' &&
      isTypedAnswerCorrect(answer, correct, ctx.typingRules)
    );
  },

  // HTML members are rendered via v-html: text is escaped (BUG-21).
  feedbackAnswerHtml(answer) {
    return escapeText(answer);
  },

  feedbackCorrectAnswer(item, ctx) {
    return escapeText(correctAnswer(item, ctx));
  },

  feedbackSubtitle(questionText) {
    return escapeText(questionText);
  },

  helpAnswer(item, ctx) {
    return escapeText(correctAnswer(item, ctx));
  },

  spokenAnswer(answer) {
    return answer;
  },

  /** Vocabulary StepDrillCard watchEffect. */
  questionText(item, ctx) {
    const directionSource = isTargetToSource(ctx.drillType?.qa_settings)
      ? 'target'
      : 'source';
    return item?.[directionSource] || '';
  },

  /** Vocabulary StepResultsCard `results` row (item found, id valid). */
  resultRow(item, id, tries, ctx) {
    const targetToSource = isTargetToSource(ctx.drillType?.qa_settings);
    const drillSource = targetToSource ? 'target' : 'source';
    const drillTarget = !targetToSource ? 'target' : 'source';
    return {
      id,
      source: item?.[drillSource],
      correct: item?.[drillTarget],
      tries: tries.map((tryItem: Try) => {
        return {
          duration: tryItem.duration,
          answer: tryItem?.isCorrect ? item?.[drillTarget] : tryItem.answer,
          isCorrect: tryItem.isCorrect,
          isHelp: tryItem.isHelp,
          ...(tryItem.isTimeout ? { isTimeout: true } : {}),
        };
      }),
    };
  },
};

/**
 * Vocabulary StepSolveCard `choices`. Distractors never repeat the correct
 * answer (BUG-25) and are a random sample of the other targets (BUG-10).
 */
export function vocabularyChoices(
  orderedStackSource: VocabularyItem[],
  currentStackItem: VocabularyItem | undefined,
  drillType: DrillVocabulary['attributes'] | undefined,
) {
  const qaSettings = drillType?.qa_settings as DrillQaSettings['attributes'];
  const directionTarget = !isTargetToSource(qaSettings) ? 'target' : 'source';

  if (
    (qaSettings?.reply_method as DrillQaReplyMethod) === 'Clicking the Reply' &&
    (!currentStackItem || (qaSettings?.number_of_options ?? 0) < 2)
  ) {
    // The distractor is never the correct answer (BUG-25); none left → one choice.
    const correct = currentStackItem?.[directionTarget];
    const distractor = orderArray(
      (orderedStackSource?.map((c) => c?.[directionTarget]) ?? []).filter(
        (c) => c !== correct,
      ),
      'random',
    )?.[0];
    return distractor === undefined ? [correct] : [correct, distractor];
  }

  if (
    (orderedStackSource?.length ?? 0) === (qaSettings?.number_of_options ?? 0)
  )
    return (
      orderArray(
        orderedStackSource?.map((c) => c?.[directionTarget]),
        'random',
      ) ?? []
    );

  if (qaSettings?.number_of_options === 0) return [];

  if (qaSettings?.number_of_options === 1)
    return [currentStackItem?.[directionTarget]];

  // Distractors: a random sample of the other (distinct) targets (BUG-10).
  const correct = currentStackItem?.[directionTarget];
  const others = [
    ...new Set(
      (orderedStackSource?.map((c) => c?.[directionTarget]) ?? []).filter(
        (c) => c !== correct,
      ),
    ),
  ];
  const distractors = orderArray(others, 'random').slice(
    0,
    (qaSettings?.number_of_options ?? 0) - 1,
  );
  const options = orderArray([...distractors, correct], 'random');
  const set = new Set(options);

  return [...set];
}

/** The answer the current item expects (picks the speech language). */
export function vocabularyItemCorrect(
  currentStackItem: VocabularyItem | undefined,
  drillType: DrillVocabulary['attributes'] | undefined,
): string | undefined {
  const qaSettings = drillType?.qa_settings as DrillQaSettings['attributes'];
  return isTargetToSource(qaSettings)
    ? currentStackItem?.source
    : currentStackItem?.target;
}
